/* Living Light — Atelier motion + commerce enhancements (Oct 2026).
   Dependency-free. Everything degrades to static content without JS or with reduced motion. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.documentElement;

  /* Photos in grids, rails and the product gallery get the curtain reveal */
  const tagPhotos = (scope = document) => {
    const selector = [
      '.ll-grid .card__media',
      '.ll-rail .card__media',
      '.related-products .card__media',
      '.product__media-list .product__media-item:not(:first-child) .product-media-container',
    ].join(',');
    scope.querySelectorAll(selector).forEach((el, i) => {
      if (el.hasAttribute('data-ll-reveal')) return;
      el.setAttribute('data-ll-reveal', 'photo');
      el.style.setProperty('--ll-delay', (i % 4) * 110);
    });
  };

  /* Reveal on scroll ------------------------------------------------------ */
  const initReveals = (scope = document) => {
    const items = scope.querySelectorAll('[data-ll-reveal]:not(.is-in)');
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    // Clip-path masks have no visible area until revealed, so watch their parent instead.
    const targets = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          (targets.get(entry.target) || []).forEach((el) => el.classList.add('is-in'));
          targets.delete(entry.target);
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    items.forEach((el) => {
      const clipped = el.dataset.llReveal === 'mask' || el.dataset.llReveal === 'photo';
      const watched = clipped && el.parentElement ? el.parentElement : el;
      if (!targets.has(watched)) targets.set(watched, []);
      targets.get(watched).push(el);
      io.observe(watched);
    });

    // Fast flings and anchor jumps can skip an element entirely; reveal anything already scrolled past.
    let pending = false;
    const sweep = () => {
      pending = false;
      targets.forEach((els, watched) => {
        if (watched.getBoundingClientRect().top < window.innerHeight) {
          els.forEach((el) => el.classList.add('is-in'));
          io.unobserve(watched);
          targets.delete(watched);
        }
      });
      if (!targets.size) window.removeEventListener('scroll', onScroll);
    };
    const onScroll = () => {
      if (pending) return;
      pending = true;
      setTimeout(sweep, 200);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  /* Scroll progress: sets --p (0 → 1) on [data-ll-progress] while it crosses the viewport */
  const initProgress = () => {
    const items = [...document.querySelectorAll('[data-ll-progress]')];
    if (!items.length) return;
    if (reduceMotion) {
      items.forEach((el) => el.style.setProperty('--p', 1));
      return;
    }
    const active = new Set();
    let ticking = false;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight;
      active.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const mode = el.dataset.llProgress;
        let p;
        if (mode === 'pin') {
          const distance = rect.height - vh;
          p = distance > 0 ? -rect.top / distance : 0;
        } else if (mode === 'exit') {
          p = -rect.top / rect.height;
        } else {
          p = (vh - rect.top) / (vh + rect.height);
        }
        p = Math.min(1, Math.max(0, p));
        el.style.setProperty('--p', p.toFixed(4));
        if (p >= 0.5) el.classList.add('is-lit');
        if (mode === 'pin') {
          const steps = Number(el.dataset.llSteps || 1);
          const index = Math.min(steps - 1, Math.floor(p * steps));
          if (el.dataset.llIndex !== String(index)) {
            el.dataset.llIndex = index;
            el.dispatchEvent(new CustomEvent('ll:step', { detail: index }));
          }
        }
      });
    };
    const request = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle('is-live', entry.isIntersecting);
        if (entry.isIntersecting) active.add(entry.target);
        else active.delete(entry.target);
      });
      update();
    });
    items.forEach((el) => io.observe(el));
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
  };

  /* Pinned story: active chapter drives text + image crossfade */
  const initStories = () => {
    document.querySelectorAll('[data-ll-story]').forEach((story) => {
      const chapters = story.querySelectorAll('[data-ll-chapter]');
      const media = story.querySelectorAll('[data-ll-chapter-media]');
      const counter = story.querySelector('[data-ll-story-index]');
      const set = (index) => {
        chapters.forEach((el, i) => el.classList.toggle('is-active', i === index));
        media.forEach((el, i) => el.classList.toggle('is-active', i === index));
        if (counter) counter.textContent = String(index + 1).padStart(2, '0');
      };
      story.addEventListener('ll:step', (event) => set(event.detail));
      set(0);
    });
  };

  /* Header state ------------------------------------------------------------ */
  const initHeader = () => {
    let last = null;
    const onScroll = () => {
      const scrolled = window.scrollY > 24;
      if (scrolled !== last) {
        root.classList.toggle('ll-scrolled', scrolled);
        last = scrolled;
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  /* Rails: previous / next buttons for scroll-snap rails ------------------- */
  const initRails = (scope = document) => {
    scope.querySelectorAll('[data-ll-rail]').forEach((rail) => {
      const track = rail.querySelector('[data-ll-rail-track]');
      const prev = rail.querySelector('[data-ll-rail-prev]');
      const next = rail.querySelector('[data-ll-rail-next]');
      if (!track || rail.dataset.llRailReady) return;
      rail.dataset.llRailReady = 'true';
      const step = () => {
        const item = track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return item ? item.getBoundingClientRect().width + gap : 300;
      };
      const sync = () => {
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      };
      const behavior = reduceMotion ? 'auto' : 'smooth';
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior }));
      track.addEventListener('scroll', sync, { passive: true });
      sync();
    });
  };

  /* Product page: sticky purchase bar + add-to-cart feedback --------------- */
  const initProduct = () => {
    const bar = document.querySelector('[data-ll-sticky-atc]');
    const form = document.querySelector('product-form form[action*="/cart/add"]');
    const submit = form?.querySelector('[type="submit"]');
    if (bar && submit) {
      bar.inert = true;
      let shown = false;
      let queued = false;
      const check = () => {
        queued = false;
        const passed = submit.getBoundingClientRect().bottom < 0;
        if (passed === shown) return;
        shown = passed;
        bar.classList.toggle('is-visible', passed);
        bar.inert = !passed;
      };
      window.addEventListener(
        'scroll',
        () => {
          if (queued) return;
          queued = true;
          setTimeout(check, 100);
        },
        { passive: true }
      );
      check();
      bar.querySelector('[data-ll-sticky-buy]')?.addEventListener('click', () => {
        submit.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        submit.click();
      });
    }

    if (submit && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source !== 'product-form') return;
        submit.classList.remove('ll-added');
        void submit.offsetWidth;
        submit.classList.add('ll-added');
      });
    }
  };

  /* Christmas order countdown: real fixed deadline, removed once passed (never resets).
     Digits count up on first view, then roll whenever a value changes. */
  const initDeadlines = () => {
    document.querySelectorAll('[data-ll-deadline]').forEach((box) => {
      const end = Date.parse(box.dataset.llDeadline);
      const units = box.querySelector('[data-ll-deadline-units]');
      const fallback = box.querySelector('[data-ll-deadline-fallback]');
      if (Number.isNaN(end) || !units) return;
      const nums = {
        d: units.querySelector('[data-unit="d"]'),
        h: units.querySelector('[data-unit="h"]'),
        m: units.querySelector('[data-unit="m"]'),
      };
      const pad = (n) => String(n).padStart(2, '0');
      const parts = () => {
        const left = end - Date.now();
        if (left <= 0) return null;
        return {
          d: Math.floor(left / 86400000),
          h: Math.floor((left % 86400000) / 3600000),
          m: Math.floor((left % 3600000) / 60000),
        };
      };
      const remove = () => {
        if (box.nextElementSibling?.classList.contains('ll-xmas__note')) box.nextElementSibling.remove();
        box.remove();
      };
      const write = (values, roll) => {
        Object.entries(values).forEach(([key, value]) => {
          const el = nums[key];
          const text = pad(value);
          if (el.textContent === text) return;
          el.textContent = text;
          if (roll && !reduceMotion) {
            el.classList.remove('is-rolling');
            void el.offsetWidth;
            el.classList.add('is-rolling');
          }
        });
        box.setAttribute(
          'aria-label',
          `Christmas delivery in Ireland: ${values.d} days ${values.h} hours left to order, order by 9 December`
        );
      };

      const first = parts();
      if (!first) return remove();
      fallback.hidden = true;
      units.hidden = false;

      const countUp = () => {
        if (reduceMotion) return write(first, false);
        const start = performance.now();
        const duration = 1400;
        const step = (now) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 4);
          write({ d: Math.round(first.d * eased), h: Math.round(first.h * eased), m: Math.round(first.m * eased) }, false);
          if (t < 1) requestAnimationFrame(step);
          else write(parts() || first, false);
        };
        requestAnimationFrame(step);
      };

      write(first, false);
      const reveal = () => {
        box.classList.add('is-in');
        countUp();
      };
      if ('IntersectionObserver' in window && !reduceMotion) {
        const io = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          io.disconnect();
          reveal();
        }, { threshold: 0.4 });
        io.observe(box);
      } else {
        reveal();
      }

      const timer = setInterval(() => {
        const now = parts();
        if (!now) {
          clearInterval(timer);
          remove();
          return;
        }
        if (box.classList.contains('is-in')) write(now, true);
      }, 15000);
    });
  };

  /* Collection grids: cards rise in a soft stagger; re-applied after filtering/sorting */
  const initGrids = () => {
    const tag = () => {
      document.querySelectorAll('.product-grid > .grid__item:not([data-ll-reveal])').forEach((item, i) => {
        item.setAttribute('data-ll-reveal', '');
        item.style.setProperty('--ll-delay', (i % 4) * 90);
      });
      tagPhotos();
      initReveals();
    };
    tag();
    const container = document.getElementById('ProductGridContainer');
    if (container) new MutationObserver(tag).observe(container, { childList: true, subtree: true });
  };

  /* Cart icon bump on every successful add */
  const initCartBump = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      const icon = document.getElementById('cart-icon-bubble');
      if (!icon || reduceMotion) return;
      icon.classList.remove('ll-bump');
      void icon.offsetWidth;
      icon.classList.add('ll-bump');
    });
  };

  /* Video hero slider: autoplay with pause, arrows, dots; only the visible slide's video plays */
  const initVideoHero = (scope = document) => {
    scope.querySelectorAll('[data-ll-vhero]').forEach((hero) => {
      if (hero.dataset.llReady) return;
      hero.dataset.llReady = 'true';
      const slides = [...hero.querySelectorAll('[data-ll-vhero-slide]')];
      const dots = [...hero.querySelectorAll('[data-ll-vhero-dot]')];
      const pauseBtn = hero.querySelector('[data-ll-vhero-pause]');
      const interval = (Number(hero.dataset.interval) || 7) * 1000;
      let index = 0;
      let timer = null;
      let paused = reduceMotion;
      // Videos wait until the page has loaded (poster image is the LCP) and are skipped on data-saver / slow links.
      const conn = navigator.connection || {};
      const allowVideo = !conn.saveData && !/(^|-)2g$/.test(conn.effectiveType || '');
      let pageLoaded = document.readyState === 'complete';
      const play = (slide) => {
        const video = slide.querySelector('video');
        if (!video || !allowVideo || !pageLoaded) return;
        if (video.dataset.src && !video.src) video.src = video.dataset.src;
        if (video.preload === 'none') video.preload = 'auto';
        if (!video.dataset.llBound) {
          video.dataset.llBound = 'true';
          video.addEventListener('playing', () => video.classList.add('is-playing'));
        }
        video.play().catch(() => {});
      };
      if (!pageLoaded) {
        window.addEventListener('load', () => {
          setTimeout(() => {
            pageLoaded = true;
            if (!paused) play(slides[index]);
          }, 1200);
        }, { once: true });
      }
      const stop = (slide) => slide.querySelector('video')?.pause();
      const go = (next) => {
        if (!slides.length) return;
        stop(slides[index]);
        slides[index].classList.remove('is-active');
        dots[index]?.removeAttribute('aria-current');
        index = (next + slides.length) % slides.length;
        slides[index].classList.add('is-active');
        dots[index]?.setAttribute('aria-current', 'true');
        play(slides[index]);
        schedule();
      };
      const schedule = () => {
        clearTimeout(timer);
        if (!paused && slides.length > 1) timer = setTimeout(() => go(index + 1), interval);
      };
      const setPaused = (value) => {
        paused = value;
        hero.classList.toggle('is-paused', paused);
        if (pauseBtn) {
          pauseBtn.setAttribute('aria-pressed', String(paused));
          pauseBtn.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
          pauseBtn.innerHTML = paused
            ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5l11 7-11 7z" fill="currentColor"/></svg>'
            : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5h3v14H8zM13 5h3v14h-3z" fill="currentColor"/></svg>';
        }
        if (paused) stop(slides[index]);
        else play(slides[index]);
        schedule();
      };
      hero.querySelector('[data-ll-vhero-prev]')?.addEventListener('click', () => go(index - 1));
      hero.querySelector('[data-ll-vhero-next]')?.addEventListener('click', () => go(index + 1));
      dots.forEach((dot, i) => dot.addEventListener('click', () => go(i)));
      pauseBtn?.addEventListener('click', () => setPaused(!paused));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting && !paused) play(slides[index]);
          else stop(slides[index]);
        }).observe(hero);
      }
      setPaused(paused);
    });
  };

  /* Video reviews: tap to play with sound; only one plays at a time */
  const initVideoReviews = () => {
    document.querySelectorAll('[data-ll-vrev-play]').forEach((btn) => {
      const media = btn.closest('[data-ll-vrev-media]');
      const video = media?.querySelector('video');
      if (!video) return;
      btn.addEventListener('click', () => {
        document.querySelectorAll('[data-ll-vrev-media].is-playing video').forEach((v) => {
          v.pause();
          v.closest('[data-ll-vrev-media]').classList.remove('is-playing');
        });
        video.muted = false;
        video.controls = true;
        video.play().catch(() => {});
        media.classList.add('is-playing');
      });
      video.addEventListener('pause', () => media.classList.remove('is-playing'));
    });
  };

  /* Add-to-cart button and sticky bar carry the selected variant's price */
  const initAtcPrice = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const { sectionId, html, variant } = event?.data || {};
      if (!html) return;
      const priceEl = html.getElementById(`price-${sectionId}`);
      const text = (priceEl?.querySelector('.price-item--sale, .price__regular .price-item--regular') || priceEl)?.textContent.trim().split(/\s{2,}/)[0].replace(/\s*[A-Z]{3}$/, '');
      document.querySelectorAll('[data-ll-atc-price]').forEach((el) => {
        el.hidden = !variant?.available;
        if (text) el.textContent = text;
      });
      const sticky = document.querySelector('[data-ll-sticky-atc] .ll-sticky-atc__price');
      if (sticky && text) sticky.textContent = text;
    });
  };

  /* Upsell "add the set": add via AJAX and open the cart drawer; falls back to a normal post */
  const initUpsell = () => {
    document.querySelectorAll('.ll-upsell__form').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer || typeof drawer.renderContents !== 'function' || !window.routes) return;
        event.preventDefault();
        const button = form.querySelector('[type="submit"]');
        button?.setAttribute('aria-busy', 'true');
        try {
          const body = new FormData(form);
          body.append('sections', drawer.getSectionsToRender().map((section) => section.id));
          body.append('sections_url', window.location.pathname);
          const response = await fetch(`${window.routes.cart_add_url}`, {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body,
          });
          const json = await response.json();
          if (json.status) throw new Error(json.description || 'Could not add');
          drawer.classList.remove('is-empty');
          drawer.renderContents(json);
          if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'll-upsell', cartData: json });
        } catch (error) {
          form.submit();
        } finally {
          button?.removeAttribute('aria-busy');
        }
      });
    });
  };

  /* Muted videos marked data-ll-autoplay-visible play only while on screen */
  const initVisibleVideos = () => {
    const videos = document.querySelectorAll('video[data-ll-autoplay-visible], .ll-film-strip video');
    if (!videos.length || reduceMotion || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) {
          target.preload = 'auto';
          target.play().catch(() => {});
        } else {
          target.pause();
        }
      });
    }, { threshold: 0.25 });
    videos.forEach((video) => io.observe(video));
  };

  /* Long collection intros collapse to three lines with a Read more toggle */
  const initReadMore = () => {
    document.querySelectorAll('[data-ll-more]').forEach((box) => {
      const desc = box.firstElementChild;
      const toggle = box.querySelector('[data-ll-more-toggle]');
      if (!desc || !toggle) return;
      box.classList.add('is-clamped');
      if (desc.scrollHeight <= desc.clientHeight + 4) {
        box.classList.remove('is-clamped');
        return;
      }
      toggle.hidden = false;
      toggle.addEventListener('click', () => {
        const open = box.classList.toggle('is-clamped') === false;
        toggle.setAttribute('aria-expanded', String(open));
        toggle.textContent = open ? 'Read less' : 'Read more';
      });
    });
  };

  /* Contact page: phone is not collected */
  const initContact = () => {
    document.querySelector('#ContactForm-phone')?.closest('.field')?.remove();
  };

  const init = () => {
    root.classList.add('ll-ready');
    tagPhotos();
    initReveals();
    initProgress();
    initStories();
    initHeader();
    initRails();
    initProduct();
    initContact();
    initDeadlines();
    initGrids();
    initCartBump();
    initVideoHero();
    initVideoReviews();
    initAtcPrice();
    initUpsell();
    initVisibleVideos();
    initReadMore();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => {
    initReveals(event.target);
    initRails(event.target);
    initVideoHero(event.target);
  });
})();
